<template>
  <div style="width: 100%; height: 100%">
    <!-- Filters -->
    <v-row no-gutters>
      <attribute-filter-panel
        title-localisation="attributes.filters.filterByAttributes"
        filter-count-localisation="attributes.filters.numApplied"
        enable-hierarchy-filters
        enable-attribute-description-filter
        :enable-attribute-filters="false"
        :enable-non-attribute-filters="false"
        :enable-store-group-chain-filter="false"
        :enable-tool-store-group-filter="false"
        :enable-client-product-key-filter="false"
        :enable-similarity-group-filter="false"
        :enable-cann-group-filter="false"
        :hierarchy-filter-level="hierarchyFilterLevel"
        :filter-rules="anchorMarginFilter"
        @attributeFilterChange="setFilterRules"
      />
      <v-alert :value="missingFilters" color="warning" icon="warning" outlined>
        {{ $t(`anchorMargin.missingFiltersWarning`) }}
      </v-alert>
    </v-row>
    <!-- Toggles -->
    <v-flex v-if="anchorMargins.length" xs4>
      <div class="toggles ml-2 mr-2 mb-2">
        <div class="switch-prefix mr-2">
          <v-label>{{ $t('anchorMargin.toggles.cascadeSameValues') }}</v-label>
        </div>
        <v-switch
          v-model="cascadeProportionately"
          :label="$t('anchorMargin.toggles.cascadeProportionately')"
          :disabled="editsExist || isSaving"
          class="mr-4 pt-0"
          primary
          dense
          hide-details
        />
      </div>
      <div class="toggles ml-2 mr-2 mb-2">
        <div class="switch-prefix">
          <v-label>{{ $t('anchorMargin.toggles.lock') }}</v-label>
          <v-icon size="1.8rem" class="mr-2" right>mdi-lock</v-icon>
        </div>
        <v-switch
          v-model="editMode"
          :disabled="editsExist || isSaving"
          class="mr-4 pt-0"
          primary
          dense
          hide-details
          @change="toggleEditMode"
        >
          <template v-slot:label>
            {{ $t('anchorMargin.toggles.edit') }}
            <v-icon size="1.8rem" right>mdi-pencil</v-icon>
          </template>
        </v-switch>
      </div>
    </v-flex>
    <!-- Table -->
    <ag-grid-vue
      v-if="!missingFilters"
      :style="{ width: '100%', height: `${dataTableHeight}px` }"
      class="ag-theme-custom ag-anchor-margins-grid"
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      @grid-ready="onGridReady"
    />
    <!-- Footer -->
    <div v-if="anchorMargins.length" class="anchor-margins-footer">
      <div class="anchor-margins-footer__btn-container">
        <v-btn :disabled="isSaving" class="grid-btn" secondary small depressed @click="resetGrid">{{
          $t('inputs.cancel')
        }}</v-btn>

        <v-btn
          small
          color="primary"
          :loading="isSaving"
          :disabled="isSaving"
          depressed
          @click="saveChanges"
        >
          {{ $t('actions.save') }}
          <v-icon small>$import</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-restricted-syntax */
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import { mapActions, mapState } from 'vuex';
import { get, set, isEmpty, isNil, isNaN, cloneDeep, round } from 'lodash';
import featureFlagsMixin from '../../../mixins/featureFlags';
import tablesMixin from '../../../mixins/tables';
import inputScreensTableMixin from '../../../mixins/inputScreensTable';
import { categoryLevel, pricingGroupLevel, architectureGroupLevel } from '@enums/hierarchy';
import { similarityGroupOption, cannGroupOption } from '@enums/filter-options';
import { formatFilters } from '../../../store/utils/filters';
import {
  getMarginTotal,
  getMarginPercentage,
  getMarginTotalRemainderPerItem,
  cascadeDown,
} from '@sharedModules/helpers/anchor-margin.helpers';
import { percentageNumericCol } from '../../../utils/inputs-grid-utils';
import sortStoreGroups from '../../../utils/sort-store-groups-util';
import CustomTooltip from '../custom-tooltip.vue';
import CellRenderer from './cell-renderer.vue';

const availableHierarchyLevels = {
  categoryLevel,
  pricingGroupLevel,
  architectureGroupLevel,
};

export default {
  name: 'AnchorGrid',
  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    CustomTooltip,
    // eslint-disable-next-line vue/no-unused-components
    CellRenderer,
  },
  mixins: [featureFlagsMixin, tablesMixin, inputScreensTableMixin],
  data() {
    return {
      originalAnchorMargins: [], // initial state with original loaded values
      intermediaryAnchorMargins: [], // current grid state before the cascade of an update
      anchorMargins: [], // current state
      editsExist: false,
      editMode: true,
      evaluatedLockedCells: {}, // controls locked cells that have been checked during a cascade
      cascadeProportionately: true, // default to proportional cascading
      hierarchy: [],
      selectedFilterParams: {},
      isLoadingData: false,
      isSaving: false,
      gridApi: null,
      gridColumnApi: null,
      gridReadyPromise: null,
      gridOptions: {
        singleClickEdit: true,
        stopEditingWhenCellsLoseFocus: true,
        enableFillHandle: true,
        fillHandleDirection: 'y',
        enableRangeSelection: true,
        tooltipShowDelay: 200,
        accentedSort: true,
        // allows for collapsing/expanding of grouped rows
        treeData: true,
        getDataPath: data => {
          // Required for working with treeData, so that it knows the correct path for row grouping
          return data.aggPath;
        },
        // used by getRowNode(rowNodeKey)
        getRowId: params => this.getKey(params.data),
        getMainMenuItems: this.getColumnMenuItems,
        groupDefaultExpanded: 0,
        autoGroupColumnDef: {
          headerName: '',
          pinned: 'left',
          filter: false,
          floatingFilter: false,
          suppressColumnsToolPanel: true,
          suppressNavigable: true,
          suppressFillHandle: true,
          minWidth: 20,
          maxWidth: 110,
          cellRendererParams: {
            suppressCount: true,
            innerRenderer: () => {
              return '';
            },
          },
        },
        defaultColDef: {
          resizable: true,
          filter: 'agMultiColumnFilter',
          headerClass: 'ag-product-list-grid__header',
          minWidth: 50,
          maxWidth: 150,
          flex: 1,
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          suppressMovable: true,
        },
        headerHeight: 27,
        overlayNoRowsTemplate: `<span class="ag-overlay-loading-center">${this.$t(
          'grid.applyFilter'
        )}</span>`,
        overlayLoadingTemplate: `<span class="ag-overlay-loading-center">${this.$t(
          'grid.loading'
        )}</span>`,
      },
    };
  },

  computed: {
    ...mapState('storeGroupRelationships', ['storeGroupChains', 'storegroupKeyNameMap']),
    ...mapState('attributes', ['attributeMetadata']),
    ...mapState('filters', ['anchorMarginFilter']),
    ...mapState('clientConfig', ['toggleLogic', 'storeGroupOrderConfig']),

    columnDefs() {
      return [
        {
          headerName: this.$t('anchorMargin.headers.productInfo'),
          groupId: 'productInfo',
          marryChildren: true,
          pinned: 'left',
          children: this.productInfoColumns,
        },
        {
          headerName: this.$t('anchorMargin.headers.marginInfo'),
          groupId: 'margin',
          marryChildren: true,
          children: this.marginColumns,
        },
      ];
    },

    /* Readonly columns */
    productInfoColumns() {
      const columns = [
        // Hierarchy columns -> Category, PG, AG.
        ...Object.values(availableHierarchyLevels).reduce((acc, level) => {
          if (this.selectedFilterParams[`hierarchy.${level}.id`]) {
            acc.push({
              headerName: this.$t(`anchorMargin.headers.hierarchy.hierarchyLevels.${level}`),
              field: `hierarchy.${level}`,
              valueGetter: params => {
                if (!params.data || !params.data[`hierarchy.${level}`]) return;
                return this.hierarchy.find(
                  h => h.levelEntryKey === params.data[`hierarchy.${level}`]
                ).levelEntryDescription;
              },
              width: 200,
              maxWidth: 300,
            });
          }
          return acc;
        }, []),
        // Attribute columns
        ...get(this.selectedFilterParams, 'attributeKey.$in', []).map((attr, ix) => {
          let headerName;
          if (attr === similarityGroupOption) {
            headerName = this.$t('pricing.similarityGroup');
          } else if (attr === cannGroupOption) {
            headerName = this.$t('pricing.cannGroup');
          } else {
            headerName = get(
              this.attributeMetadata.find(attrMetadata => attrMetadata.attributeKey === attr),
              'attributeDescription'
            );
          }

          return {
            headerName,
            field: `attributes.${ix}`,
            width: 200,
            maxWidth: 300,
          };
        }),
      ];

      return columns;
    },

    /* Editable columns */
    marginColumns() {
      const anchorMarginColumn = {
        maxWidth: 110,
        tooltipValueGetter: this.tooltipValueGetter,
        tooltipComponent: 'CustomTooltip',
        cellStyle: this.getCellStyle,
        cellClassRules: {
          'diff-background': this.hasDiff,
          'locked-background': this.isLocked,
        },
        ...percentageNumericCol,
        cellEditorParams: params => {
          return {
            value: this.format(params.value),
          };
        },
        cellRenderer: 'CellRenderer',
        cellRendererParams: params => {
          const locked = this.isLocked(params);
          return {
            secondaryValue: this.getDelta(params),
            icon: locked ? 'mdi-lock' : '',
            editable: this.editMode && !locked,
          };
        },
        onCellClicked: params => {
          if (!this.editMode) {
            this.cellLockChanged(params);
          }
        },
        editable: params => {
          return this.editMode && !this.isLocked(params);
        },
      };

      const storeGroupColumns = (chainId, toolStoreGroupKeys) => {
        const toolStoreGroups = toolStoreGroupKeys.map(tsgKey => ({
          tsgKey,
          description: this.storegroupKeyNameMap[tsgKey],
        }));
        const sortedTSGs = sortStoreGroups(
          toolStoreGroups,
          this.storeGroupOrderConfig,
          'description'
        );
        return sortedTSGs.map(({ tsgKey }) => {
          return {
            headerName: this.storegroupKeyNameMap[tsgKey],
            colId: `totals.chains.${chainId}.storeGroups.${tsgKey}.anchorMargin`,
            field: `totals.chains.${chainId}.storeGroups.${tsgKey}.anchorMargin`,
            onCellValueChanged: this.cellValueChanged,
            ...anchorMarginColumn,
          };
        });
      };

      const storeGroupChainColumns = this.storeGroupChains.map(chain => {
        return {
          headerName: chain.description,
          field: 'chain',
          minWidth: 110,
          children: [
            {
              headerName: '',
              colId: `totals.chains.${chain._id}.anchorMargin`,
              field: `totals.chains.${chain._id}.anchorMargin`,
              onCellValueChanged: this.cellValueChanged,
              ...anchorMarginColumn,
            },
            ...storeGroupColumns(chain._id, chain.toolStoreGroupKeys),
          ],
        };
      });

      const aggregationTotalColumn = {
        headerName: this.$t('anchorMargin.headers.totalMargin'),
        colId: 'totals.anchorMargin',
        field: 'totals.anchorMargin',
        onCellValueChanged: this.cellValueChanged,
        ...anchorMarginColumn,
      };

      const columns = [aggregationTotalColumn, ...storeGroupChainColumns];

      return columns;
    },

    columnTree() {
      return ['totals', 'chains', 'storeGroups'];
    },

    formattedFilters() {
      return formatFilters({
        where: this.anchorMarginFilter,
        rootState: this.$store.state,
        ignoreHierarchyLevel: false,
      });
    },

    hierarchyFilterLevel() {
      return this.toggleLogic.anchorMarginsHierarchyAggLevel;
    },

    missingFilters() {
      return isEmpty(this.selectedFilterParams);
    },
  },

  mounted() {},
  beforeDestroy() {},

  async created() {
    // Fetch hierarchy data for filters
    this.hierarchy = await this.fetchHierarchy();

    // Fetch grid data if there's any filters applied
    if (!isEmpty(this.formattedFilters)) {
      this.fetchGridData();
    }
  },

  methods: {
    ...mapActions('anchorMargins', ['fetchAnchorMargins', 'updateAnchorMargins']),
    ...mapActions('hierarchy', ['fetchHierarchy']),
    ...mapActions('filters', ['setSelectedFilter']),

    /* -------------------------------------------------- *
     *           Grid / Data loading functions            *
     * -------------------------------------------------- */

    onGridReady(params) {
      this.gridApi = params.api;
      this.calculateDataTableHeight();
      this.gridApi.showLoadingOverlay();
      this.gridColumnApi = params.columnApi;

      // Apply export method overrides to exclude auto-group column
      this.configureGridExports();

      // Grid now ready to be used.
      if (this.gridReadyPromise) {
        this.gridReadyPromise(true);
      }
    },

    async fetchGridData() {
      // Show loading state
      this.isLoadingData = true;
      if (this.gridApi) {
        this.gridApi.showLoadingOverlay();
      }

      try {
        // Get current filters
        this.selectedFilterParams = this.formattedFilters;

        // Fetch anchor margins
        this.anchorMargins = await this.fetchAnchorMargins(this.selectedFilterParams);
        // Store original values to be used for comparison/restore
        this.originalAnchorMargins = cloneDeep(this.anchorMargins);

        // Grid might not be ready when data is here. If so, wait.
        if (!this.gridApi) {
          await new Promise(resolve => {
            this.gridReadyPromise = resolve;
          });
        }

        // Set grid data once grid is ready
        this.setGridData(this.anchorMargins);

        if (!isEmpty(this.anchorMargins)) {
          this.gridApi.hideOverlay();
        } else {
          this.gridApi.showNoRowsOverlay();
        }
      } catch (error) {
        console.error('Error fetching grid data:', error);
        // Handle error and hide loading overlay
        if (this.gridApi) {
          this.gridApi.hideOverlay();
        }
      } finally {
        this.isLoadingData = false;
      }
    },

    setGridData(anchorMargins) {
      const data = anchorMargins;
      this.gridApi.setGridOption('rowData', data);
    },

    /* Refreshes parent and child rows in the ag-grid after updating rowData reference values */
    refreshRelatedCells(rowNodeKey) {
      const rowNode = this.getRowNode(rowNodeKey);
      if (!rowNode) return;

      const refreshParents = node => {
        let currentNode = node;
        const rowsToRefresh = [currentNode];

        // Traverse up the tree to find all parents/grandparents
        while (currentNode.parent) {
          rowsToRefresh.push(currentNode.parent);
          currentNode = currentNode.parent;
        }
        this.gridApi.refreshCells({ rowNodes: rowsToRefresh, force: true });
      };

      const refreshChildren = node => {
        if (node.childrenAfterSort && node.childrenAfterSort.length > 0) {
          const rowsToRefresh = node.childrenAfterSort;

          // Refresh all direct child nodes
          this.gridApi.refreshCells({
            rowNodes: rowsToRefresh,
            force: true,
          });

          // Recursively refresh grandchildren
          node.childrenAfterSort.forEach(childNode => {
            refreshChildren(childNode);
          });
        }
      };

      refreshParents(rowNode);
      refreshChildren(rowNode);
    },

    refreshGridCells() {
      this.gridApi.refreshCells({ force: true });
    },

    async setFilterRules(filterRules) {
      this.setSelectedFilter({ filterName: 'anchorMarginFilter', filterValue: filterRules });
      await this.fetchGridData();
    },

    /* -------------------------------------------------- *
     *           Grid util getter functions               *
     * -------------------------------------------------- */

    getKey(row) {
      if (!row || !row.aggPath) {
        return null;
      }
      // matches rowNodeKey
      return row.aggPath.join('::');
    },

    getUniqueCellPathKey(item, colPath) {
      // Combination of the row and col identifiers (rowNodeKey and colId)
      return `${this.getKey(item)}::${colPath}`;
    },

    getRowNode(rowNodeKey) {
      return this.gridApi.getRowNode(rowNodeKey);
    },

    getParentRowKey(rowNodeKey) {
      if (!rowNodeKey) return null;

      // For rowNodeKey "pgId::agId::attrVal1", it'll return "pgId::agId"
      return rowNodeKey
        .split('::')
        .slice(0, -1)
        .join('::');
    },

    getColPath(colId) {
      return colId
        .split('.')
        .slice(0, -1)
        .join('.');
    },

    getSiblingColumnPaths(item, colPath, isLocked = false) {
      if (!colPath) return null;

      const parentPath = colPath
        .split('.')
        .slice(0, -1)
        .join('.');
      if (!parentPath) return null;

      const currentKey = colPath.split('.').pop();

      // For itemPath "totals.chains.chainId1.storeGroups.tsgKey1", and parentPath "totals.chains.chainId1.storeGroups"
      // it'll return ["totals.chains.chainId1.storeGroups.tsgKey2", "totals.chains.chainId1.storeGroups.tsgKey3", ...]
      // Only for siblings that contain valid data and a matching lock state
      return Object.keys(get(item, parentPath))
        .filter(key => {
          return (
            key !== currentKey &&
            this.isCellAvailable(item, `${parentPath}.${key}`) &&
            isLocked === this.isCellLocked(item, `${parentPath}.${key}`)
          );
        })
        .map(key => `${parentPath}.${key}`);
    },

    getChildColumnPaths(item, colPath, isLocked = false) {
      // For colPath "totals", return ["totals.chains.chainId1", "totals.chains.chainId2", ...]
      // For colPath "totals.chains.chainId1", return ["totals.chains.chainId1.storeGroups.tsgKey1", "totals.chains.chainId1.storeGroups.tsgKey2", ...]
      // Only for children that contain valid data and a matching lock state
      const childColumnPaths = [];

      this.columnTree.forEach(childCol => {
        const childColPath = `${colPath}.${childCol}`;
        const validColPath = get(item, childColPath);

        if (!validColPath) return;

        childColumnPaths.push(
          ...Object.keys(validColPath)
            .filter(key => {
              return (
                this.isCellAvailable(item, `${childColPath}.${key}`) &&
                isLocked === this.isCellLocked(item, `${childColPath}.${key}`)
              );
            })
            .map(key => `${childColPath}.${key}`)
        );
      });

      return childColumnPaths;
    },

    getParentColumnPath(itemPath) {
      if (!itemPath) return null;

      // For itemPath "totals.chains.chainId1.storeGroups.tsgKey1", it'll return "totals.chains.chainId1"
      return itemPath
        .split('.')
        .slice(0, -2)
        .join('.');
    },

    getRowItem(anchorMargins, params) {
      return anchorMargins.find(item => this.getKey(item) === this.getKey(params.data));
    },

    getDeepestAggregationLevelRows() {
      // Find the number of aggregations of the deepest child, e.g. pdId:agId:attrVal1 should return 3
      const deepestAggregationLevel = this.anchorMargins.reduce(
        (max, item) => Math.max(max, item.aggPath.length),
        0
      );
      // Gets the deeper items in the tree, containing the most specific filters
      // and the margins we'd like to surface to products on save
      const items = this.anchorMargins.filter(
        item => item.aggPath.length === deepestAggregationLevel
      );

      return items;
    },

    getChildColumnsByLockState(rowNode, colPath) {
      // Get locked and unlocked child columns not only based on the current row but also
      // considering parent rows, e.g. an unlocked attribute row column with a locked AG row column
      // would be considered locked
      const lockedChildCols = this.getChildColumnPaths(rowNode.data, colPath, true);
      const unlockedChildCols = this.getChildColumnPaths(rowNode.data, colPath, false);
      const childColPaths = [...lockedChildCols, ...unlockedChildCols];
      const lockedChildColsIncParents = childColPaths.filter(childColPath => {
        let currentRowNode = rowNode;
        let isLocked = false;
        while (currentRowNode.data && !isLocked) {
          isLocked =
            this.isCellAvailable(currentRowNode.data, childColPath) &&
            this.isCellLocked(currentRowNode.data, childColPath);
          currentRowNode = currentRowNode.parent;
        }
        return isLocked;
      });
      const unlockedChildColsIncParents = childColPaths.filter(childColPath => {
        let currentRowNode = rowNode;
        let isUnlocked = true;
        while (currentRowNode.data && isUnlocked) {
          isUnlocked =
            this.isCellAvailable(currentRowNode.data, childColPath) &&
            !this.isCellLocked(currentRowNode.data, childColPath);
          currentRowNode = currentRowNode.parent;
        }
        return isUnlocked;
      });

      // If there's no unlocked cols available when checking their parent rows,
      // get locked and unlocked cols only based on the current colPath, excluding parent row checks.
      return {
        locked: isEmpty(unlockedChildColsIncParents) ? lockedChildCols : lockedChildColsIncParents,
        unlocked: isEmpty(unlockedChildColsIncParents)
          ? unlockedChildCols
          : unlockedChildColsIncParents,
      };
    },

    getChildRowsByLockState(childRows, colPath) {
      // Get locked and unlocked child rows not only based on the current colPath but also
      // considering parent paths, e.g. an unlocked storeGroup column with a locked total would
      // be considered locked
      const lockedChildRowsIncParents = childRows.filter(childRow => {
        let currentColPath = colPath;
        let isLocked = false;
        while (currentColPath && !isLocked) {
          isLocked =
            this.isCellAvailable(childRow.data, currentColPath) &&
            this.isCellLocked(childRow.data, currentColPath);
          currentColPath = this.getParentColumnPath(currentColPath);
        }
        return isLocked;
      });
      const unlockedChildRowsIncParents = childRows.filter(childRow => {
        let currentColPath = colPath;
        let isUnlocked = true;
        while (currentColPath && isUnlocked) {
          isUnlocked =
            this.isCellAvailable(childRow.data, currentColPath) &&
            !this.isCellLocked(childRow.data, currentColPath);
          currentColPath = this.getParentColumnPath(currentColPath);
        }
        return isUnlocked;
      });

      // If there's no unlocked rows available when checking their parent columns,
      // get locked and unlocked rows only based on the current colPath, excluding parent col checks.
      const lockedChildRows = isEmpty(unlockedChildRowsIncParents)
        ? childRows.filter(childRow => {
            return (
              this.isCellAvailable(childRow.data, colPath) &&
              this.isCellLocked(childRow.data, colPath)
            );
          })
        : lockedChildRowsIncParents;
      const unlockedChildRows = isEmpty(unlockedChildRowsIncParents)
        ? childRows.filter(childRow => {
            return (
              this.isCellAvailable(childRow.data, colPath) &&
              !this.isCellLocked(childRow.data, colPath)
            );
          })
        : unlockedChildRowsIncParents;

      return {
        locked: lockedChildRows,
        unlocked: unlockedChildRows,
      };
    },

    /* -------------------------------------------------- *
     *       Margin update and cascading functions        *
     * -------------------------------------------------- */

    /* Updates anchorMargin and anchorMarginTotal for a given cell */
    updateCell({ item, colPath, margin, marginTotal }) {
      const currentMargin = get(item, `${colPath}.anchorMargin`);

      if (!isNil(currentMargin)) {
        set(item, `${colPath}.anchorMargin`, this.format(margin));
        set(item, `${colPath}.anchorMarginTotal`, this.format(marginTotal));

        this.editsExist = true;
      }
    },

    /* Updates a child cell based on cascadeDown logic, then returns the new margins */
    updateChildCell({ item, colPath, margin, marginDiff, marginTotalRemainder = 0 }) {
      // Get current values
      const { sales, anchorMargin: previousMargin, anchorMarginTotal: previousMarginTotal } = get(
        item,
        colPath
      );

      // Calculate new margins and include any weighted remainder from sibling columns due to parent locks
      const newMargin = cascadeDown({
        proportional: this.cascadeProportionately,
        previousMargin,
        newMargin: margin,
        parentMarginDiff: marginDiff,
      });
      const newMarginTotal =
        getMarginTotal({ marginPercentage: newMargin, sales }) + marginTotalRemainder;
      const marginTotalDiff = newMarginTotal - previousMarginTotal;

      // recalculate new margin with remainder if there is any
      const newMarginIncRemainder =
        marginTotalRemainder > 0
          ? getMarginPercentage({
              marginTotal: newMarginTotal,
              sales,
            })
          : newMargin;
      const newMarginDiff = newMarginIncRemainder - previousMargin;

      // Update child cell with new margins
      this.updateCell({
        item,
        colPath,
        margin: newMarginIncRemainder,
        marginTotal: newMarginTotal,
      });

      // Return new values for further parent/child updates
      return {
        margin: newMarginIncRemainder,
        marginDiff: newMarginDiff,
        marginTotalDiff,
      };
    },

    /* Updates child columns, identified by the column path totals.chains.{chainId}.storeGroups.{tsgKey}
     * For totals column, it will update chains and storeGroups, then from storeGroups, update parent and child rows.
     * For chains column, it will update storeGroups, then from storeGroups, update parent and child rows.
     * For storeGroups, it will not apply any updates as store-group level has no child column.
     */
    updateChildColumns({ item, colPath, childRows, margin, marginDiff }) {
      // Only update child columns if not at the last col level
      if (this.isColLastLevel(colPath)) return;

      const rowNode = this.getRowNode(this.getKey(item));
      // Get locked and unlocked available child rows
      const {
        locked: lockedChildColPaths,
        unlocked: unlockedChildColPaths,
      } = this.getChildColumnsByLockState(rowNode, colPath);

      // If there are locked child columns, we get the margin total remainder that the locked
      // columns would receive from cascadeDown and distribute it weighted towards the other unlocked child columns
      const marginTotalRemainder = lockedChildColPaths.reduce((acc, lockedChildColPath) => {
        const lockedChildColItem = get(item, lockedChildColPath);
        const {
          anchorMargin: lockedAnchorMargin,
          anchorMarginTotal: lockedMarginTotal,
          sales,
        } = lockedChildColItem;
        const newVirtualMargin = cascadeDown({
          proportional: this.cascadeProportionately,
          previousMargin: lockedAnchorMargin,
          newMargin: margin,
          parentMarginDiff: marginDiff,
        });
        const newVirtualMarginTotal = getMarginTotal({
          marginPercentage: newVirtualMargin,
          sales,
        });
        return acc + newVirtualMarginTotal - lockedMarginTotal;
      }, 0);

      // Get the margin total remainder for each child based on the absolute value of their sales
      const marginTotalRemainderPerChild = this.getMarginTotalRemaindersPerColumn(
        item,
        unlockedChildColPaths,
        marginTotalRemainder
      );

      unlockedChildColPaths.forEach(childColPath => {
        let ignoreFurtherRowLocks = false;
        const isSingleChildCol = [...lockedChildColPaths, ...unlockedChildColPaths].length === 1;
        if (isSingleChildCol) {
          // If current col is a single child col,
          // there's no need to re-apportion the same locks since the parent has already apportioned those
          ignoreFurtherRowLocks = true;
        }

        const {
          margin: newMargin,
          marginDiff: newMarginDiff,
          marginTotalDiff,
        } = this.updateChildCell({
          item,
          colPath: childColPath,
          margin,
          marginDiff,
          marginTotalRemainder: marginTotalRemainderPerChild[childColPath],
        });
        this.updateChildRows({
          childRows,
          colPath: childColPath,
          margin: newMargin,
          marginDiff: newMarginDiff,
        });
        this.updateParentRows({
          rowNodeKey: this.getKey(item),
          colPath: childColPath,
          marginTotalDiff,
          ignoreLocks: ignoreFurtherRowLocks,
        });
        this.updateChildColumns({
          item,
          colPath: childColPath,
          childRows,
          margin: newMargin,
          marginDiff: newMarginDiff,
        });
      });
    },

    /* Updates parent columns, identified by the column path totals.chains.{chainId}.storeGroups.{tsgKey}
     * For totals column, it will not apply any updates as totals level has no parent column.
     * For chains column, it will update totals.
     * For storeGroups column, it will update chains, and then totals.
     */
    updateParentColumns({ item, colPath, marginTotalDiff, ignoreLocks = false }) {
      const parentColPath = this.getParentColumnPath(colPath);
      if (!parentColPath) return;

      const columnToUpdate = get(item, parentColPath);
      const rowNodeKey = this.getKey(item);
      // If parent column is locked, the margin total diff has to be distributed to sibling columns
      if (columnToUpdate.locked) {
        if (!ignoreLocks && !this.hasLockedCellBeenEvaluated(parentColPath, rowNodeKey)) {
          this.setEvaluatedLockedCell(parentColPath, rowNodeKey);
          this.updateSiblingColumns({ item, colPath, marginTotalDiff });
        }

        return;
      }

      // Add margin total to aggregated parent margins
      const newParentMarginTotal = columnToUpdate.anchorMarginTotal + marginTotalDiff;
      const newParentMargin = getMarginPercentage({
        marginTotal: newParentMarginTotal,
        sales: columnToUpdate.sales,
      });

      // Update parent cell with new margins
      this.updateCell({
        item,
        colPath: parentColPath,
        margin: newParentMargin,
        marginTotal: newParentMarginTotal,
      });

      // Recursively update grandparent columns
      this.updateParentColumns({
        item,
        colPath: parentColPath,
        marginTotalDiff,
        ignoreLocks,
      });
    },

    /* Updates sibling columns whenever a parent is locked and values cannot be aggregated up,
     * then we distribute the weighted margin total remainder across siblings.
     */
    updateSiblingColumns({ item, colPath, marginTotalDiff }) {
      const unlockedSiblingColPaths = this.getSiblingColumnPaths(item, colPath, false);
      const rowNodeKey = this.getKey(item);
      const rowNode = this.getRowNode(rowNodeKey);
      if (!rowNode) return;

      const childRows = rowNode.childrenAfterFilter;

      // Weight the margin total remainder for each sibling based on the absolute value of their sales
      const marginTotalRemainderPerSibling = this.getMarginTotalRemaindersPerColumn(
        item,
        unlockedSiblingColPaths,
        marginTotalDiff
      );

      // Calculate new margins for each sibling, including the remainder portion
      // received from sibling columns due to parent locks
      unlockedSiblingColPaths.forEach(siblingColPath => {
        const {
          sales,
          anchorMargin: previousAnchorMargin,
          anchorMarginTotal: previousMarginTotal,
        } = get(item, siblingColPath);
        const newSiblingMarginTotal =
          previousMarginTotal - marginTotalRemainderPerSibling[siblingColPath];
        const newSiblingMargin = getMarginPercentage({
          marginTotal: newSiblingMarginTotal,
          sales,
        });
        const newMarginDiff = newSiblingMargin - previousAnchorMargin;
        const newMarginTotalDiff = newSiblingMarginTotal - previousMarginTotal;

        this.updateCell({
          item,
          colPath: siblingColPath,
          margin: newSiblingMargin,
          marginTotal: newSiblingMarginTotal,
        });
        this.updateChildRows({
          childRows,
          colPath: siblingColPath,
          margin: newSiblingMargin,
          marginDiff: newMarginDiff,
        });
        this.updateParentRows({
          rowNodeKey,
          colPath: siblingColPath,
          marginTotalDiff: newMarginTotalDiff,
        });
        this.updateChildColumns({
          item,
          colPath: siblingColPath,
          childRows,
          margin: newSiblingMargin,
          marginDiff: newMarginDiff,
        });
      });
    },

    /* Updates child rows recursively starting from the storeGroups column.
     * Child rows are identified by the ag-grid treeData node childrenAfterFilter attribute.
     */
    updateChildRows({ childRows, colPath, margin, marginDiff }) {
      // Only update child rows if at the TSG (last column) level
      // For chain and total updates, these happen as part of updateChildColumns when updating the TSG column
      if (!this.isColLastLevel(colPath)) return;

      // Get locked and unlocked available child rows
      const { locked: lockedChildRows, unlocked: unlockedChildRows } = this.getChildRowsByLockState(
        childRows,
        colPath
      );

      // If there are locked child row cols, we get the margin total remainder that the locked
      // cells would receive from cascadeDown and distribute it weighted towards the other unlocked child cells
      const marginTotalRemainder = lockedChildRows.reduce((acc, lockedChildRow) => {
        const childRowNodeKey = this.getKey(lockedChildRow.data);
        const lockedChildRowItem = this.anchorMargins.find(
          row => this.getKey(row) === childRowNodeKey
        );
        const lockedChildRowCol = get(lockedChildRowItem, colPath);
        if (isNil(lockedChildRowCol)) return acc;

        const {
          anchorMargin: lockedAnchorMargin,
          anchorMarginTotal: lockedMarginTotal,
          sales,
        } = lockedChildRowCol;
        const newVirtualMargin = cascadeDown({
          proportional: this.cascadeProportionately,
          previousMargin: lockedAnchorMargin,
          newMargin: margin,
          parentMarginDiff: marginDiff,
        });
        const newVirtualMarginTotal = getMarginTotal({
          marginPercentage: newVirtualMargin,
          sales,
        });
        return acc + newVirtualMarginTotal - lockedMarginTotal;
      }, 0);

      // Get the margin total remainder for each child based on the absolute value of their sales
      const marginTotalRemainderPerChild = this.getMarginTotalRemaindersPerRow(
        unlockedChildRows,
        colPath,
        marginTotalRemainder
      );

      unlockedChildRows.forEach(childRow => {
        const childRowNodeKey = this.getKey(childRow.data);
        const itemToUpdate = this.anchorMargins.find(row => this.getKey(row) === childRowNodeKey);
        const columnToUpdate = get(itemToUpdate, colPath);
        // If there's no margins for a specific column, we don't update it
        if (isNil(columnToUpdate)) return;

        let ignoreFurtherColLocks = false;
        const isSingleChildRow = [...lockedChildRows, ...unlockedChildRows].length === 1;
        if (isSingleChildRow) {
          // If current row is a single child row,
          // there's no need to re-apportion the same locks since the parent has already apportioned those
          ignoreFurtherColLocks = true;
        }

        // Update child row column cell
        const {
          margin: newMargin,
          marginDiff: newMarginDiff,
          marginTotalDiff,
        } = this.updateChildCell({
          item: itemToUpdate,
          colPath,
          margin,
          marginDiff,
          marginTotalRemainder: marginTotalRemainderPerChild[childRowNodeKey],
        });

        // Surface update to parent columns (chains and totals) in the same child row
        this.updateParentColumns({
          item: itemToUpdate,
          colPath,
          marginTotalDiff,
          ignoreLocks: ignoreFurtherColLocks,
        });

        // Recursively update grandchild rows
        this.updateChildRows({
          childRows: childRow.childrenAfterFilter,
          colPath,
          margin: newMargin,
          marginDiff: newMarginDiff,
        });
      });
    },

    /* Updates parent rows recursively starting from the storeGroups column.
     * Parent rows are identified by the aggPath key pgId::agId::attrVal1::attrVal2::attrVal3...
     */
    updateParentRows({ rowNodeKey, colPath, marginTotalDiff, ignoreLocks = false }) {
      const parentRowNodeKey = this.getParentRowKey(rowNodeKey);
      if (!parentRowNodeKey) return;

      const itemToUpdate = this.anchorMargins.find(row => this.getKey(row) === parentRowNodeKey);
      const columnToUpdate = get(itemToUpdate, colPath);
      // If there's no margins for a specific column, we don't update it
      if (isNil(columnToUpdate)) return;
      const { sales, anchorMarginTotal: previousMarginTotal, locked } = columnToUpdate;
      // If parent row col is locked, the margin total diff has to be distributed to sibling rows
      if (locked) {
        if (!ignoreLocks && !this.hasLockedCellBeenEvaluated(parentRowNodeKey, colPath)) {
          this.setEvaluatedLockedCell(parentRowNodeKey, colPath);
          this.updateSiblingRows({ rowNodeKey, colPath, marginTotalDiff });
        }
        return;
      }

      // Only update parent rows if at the TSG (last column) level
      // For chain and total updates, these happen as part of updateChildColumns when updating the TSG column
      if (!this.isColLastLevel(colPath)) return;

      // Add marginTotalDiff and generate new margin
      const newParentMarginTotal = previousMarginTotal + marginTotalDiff;
      const newParentMargin = getMarginPercentage({ marginTotal: newParentMarginTotal, sales });

      // Update parent row column cell
      this.updateCell({
        item: itemToUpdate,
        colPath,
        margin: newParentMargin,
        marginTotal: newParentMarginTotal,
      });

      // Surface update to parent columns (chains and totals) in the same parent row
      this.updateParentColumns({
        item: itemToUpdate,
        colPath,
        marginTotalDiff,
      });

      // Recursively update grandparent rows
      this.updateParentRows({
        rowNodeKey: parentRowNodeKey,
        colPath,
        marginTotalDiff,
        ignoreLocks,
      });
    },

    /* Updates sibling rows whenever a parent is locked and values cannot be aggregated up,
     * then we distribute the weighted margin total remainder across siblings.
     */
    updateSiblingRows({ rowNodeKey, colPath, marginTotalDiff }) {
      const rowNode = this.getRowNode(rowNodeKey);
      if (!rowNode) return;

      const parentRowNode = rowNode.parent;
      const unlockedSiblingRowNodes = parentRowNode.childrenAfterFilter.filter(siblingRowNode => {
        return (
          this.getKey(siblingRowNode.data) !== this.getKey(rowNode.data) &&
          this.isCellAvailable(siblingRowNode.data, colPath) &&
          !this.isCellLocked(siblingRowNode.data, colPath)
        );
      });

      // Weight the margin total remainder for each sibling based on the absolute value of their sales
      const marginTotalRemainderPerSibling = this.getMarginTotalRemaindersPerRow(
        unlockedSiblingRowNodes,
        colPath,
        marginTotalDiff
      );

      // Calculate new margins for each sibling, including the remainder portion
      // received from sibling rows due to parent locks
      unlockedSiblingRowNodes.forEach(unlockedSiblingRowNode => {
        const siblingRowItem = this.anchorMargins.find(
          row => this.getKey(row) === this.getKey(unlockedSiblingRowNode.data)
        );
        const childRows = unlockedSiblingRowNode.childrenAfterFilter;
        const siblingRowNodeKey = this.getKey(siblingRowItem);
        const {
          sales,
          anchorMargin: previousAnchorMargin,
          anchorMarginTotal: previousMarginTotal,
        } = get(siblingRowItem, colPath);
        const newSiblingMarginTotal =
          previousMarginTotal - marginTotalRemainderPerSibling[siblingRowNodeKey];
        const newSiblingMargin = getMarginPercentage({
          marginTotal: newSiblingMarginTotal,
          sales,
        });
        const newMarginDiff = newSiblingMargin - previousAnchorMargin;
        const newMarginTotalDiff = newSiblingMarginTotal - previousMarginTotal;

        this.updateCell({
          item: siblingRowItem,
          colPath,
          margin: newSiblingMargin,
          marginTotal: newSiblingMarginTotal,
        });
        this.updateParentColumns({
          item: siblingRowItem,
          colPath,
          marginTotalDiff: newMarginTotalDiff,
        });
        this.updateChildRows({
          childRows,
          colPath,
          margin: newSiblingMargin,
          marginDiff: newMarginDiff,
        });
        this.updateChildColumns({
          item: siblingRowItem,
          colPath,
          childRows,
          margin: newSiblingMargin,
          marginDiff: newMarginDiff,
        });
      });
    },

    /* Cascades updates applied to a cell to affected columns and rows within it's tree */
    cascadeMargins({ itemToUpdate, colPath, rowNodeKey, newValue, previousValue, childRows }) {
      const marginDiff = newValue - previousValue;
      const { sales, anchorMarginTotal: previousMarginTotal } = get(itemToUpdate, colPath);
      const newMarginTotal = getMarginTotal({ marginPercentage: newValue, sales });
      const marginTotalDiff = newMarginTotal - previousMarginTotal;

      this.updateCell({
        item: itemToUpdate,
        colPath,
        margin: newValue,
        marginTotal: newMarginTotal,
        virtualLock: true,
      });
      this.updateParentColumns({
        item: itemToUpdate,
        colPath,
        marginTotalDiff,
      });
      this.updateChildRows({
        childRows,
        colPath,
        margin: newValue,
        marginDiff,
      });
      this.updateParentRows({
        rowNodeKey,
        colPath,
        marginTotalDiff,
      });
      this.updateChildColumns({
        item: itemToUpdate,
        colPath,
        childRows,
        margin: newValue,
        marginDiff,
      });

      this.evaluatedLockedCells = {};
    },

    cellValueChanged(params) {
      const rowNodeKey = this.getKey(params.data);
      const { colId, field, valueParser } = params.colDef;
      const colPath = this.getColPath(colId);
      const previousValue = params.oldValue;
      let newValue = valueParser ? valueParser(params) : get(params.data, field);

      // Force zero if user clears the margin value
      if (isNil(newValue)) {
        newValue = 0;
        params.node.setDataValue(field, newValue);
      }

      // Skip update if there's no actual change in value
      if (this.format(previousValue) === this.format(newValue)) {
        return;
      }

      const itemToUpdate = this.getRowItem(this.anchorMargins, params);

      // Save current state before cascading
      this.setIntermediaryState(params);

      // Add a temp lock to the updated focused cell, so that we prevent circular updates from
      // cascading changes back into it
      this.toggleCellLock(itemToUpdate, colPath, true);
      this.setEvaluatedLockedCell(colPath, rowNodeKey);
      this.setEvaluatedLockedCell(rowNodeKey, colPath);

      try {
        // Cascade new values to other columns and rows
        this.cascadeMargins({
          itemToUpdate,
          colPath,
          rowNodeKey,
          newValue,
          previousValue,
          childRows: params.node.childrenAfterFilter,
        });
      } catch (e) {
        if (e instanceof RangeError) {
          console.error('Infinite loop error trying to cascade anchor margin value.', e);
        } else {
          console.error('Error trying to cascade anchor margin value.', e);
        }

        // Revert to previous state if there're errors
        this.anchorMargins = cloneDeep(this.intermediaryAnchorMargins);
        this.setGridData(this.anchorMargins);
      }

      // Revert temp lock
      this.toggleCellLock(itemToUpdate, colPath, false);

      // Refresh grid
      this.refreshGridCells();
    },

    /* -------------------------------------------------- *
     *       Locking update and cascade functions         *
     * -------------------------------------------------- */

    toggleCellLock(item, colPath, lock) {
      set(item, `${colPath}.locked`, lock);
    },

    toggleSiblingColLock(item, colPath, lock) {
      const unlockedSiblingColPaths = this.getSiblingColumnPaths(item, colPath, false);
      const parentColPath = this.getParentColumnPath(colPath);
      const parentItem = get(item, parentColPath) || {};

      /* If cell is getting locked, parent col is locked, and there's only one unlocked sibling,
       * then that sibling col should be locked.
       */
      if (lock && parentItem.locked && unlockedSiblingColPaths.length === 1) {
        this.toggleCellLock(item, unlockedSiblingColPaths[0], lock);

        // Toggle sibling, parent and child rows from sibling column
        this.toggleSiblingRowLock(item, unlockedSiblingColPaths[0], lock);
        this.toggleParentRowLock(item, unlockedSiblingColPaths[0], lock);
        this.toggleChildRowLock(item, unlockedSiblingColPaths[0], lock);

        // Toggle child cols from sibling column
        this.toggleParentColLock(item, unlockedSiblingColPaths[0], lock);
        this.toggleChildColLock(item, unlockedSiblingColPaths[0], lock);
      }
    },

    toggleParentColLock(item, colPath, lock) {
      const unlockedSiblingColPaths = this.getSiblingColumnPaths(item, colPath, false);
      const parentColPath = this.getParentColumnPath(colPath);
      if (!parentColPath) return;

      const parentRowNodeKey = this.getParentRowKey(this.getKey(item));
      const parentItem = this.anchorMargins.find(row => this.getKey(row) === parentRowNodeKey);
      const isParentRowAndColLocked =
        this.isCellLocked(item, parentColPath) && this.isCellLocked(parentItem, colPath);

      /* If cell is getting locked, and all siblings are also locked, i.e. unlockedSiblingColPaths is empty,
       * then parent col should be locked
       * If cell is getting unlocked, and all siblings are locked, i.e. unlockedSiblingColPaths is empty,
       * then parent col should be unlocked
       * If cell is getting unlocked, and both parent row and parent col are locked, then parent should be unlocked.
       */
      if (isEmpty(unlockedSiblingColPaths) || (isParentRowAndColLocked && !lock)) {
        this.toggleCellLock(item, parentColPath, lock);

        // Toggle sibling, parent and child rows from parent column
        this.toggleSiblingRowLock(item, parentColPath, lock);
        this.toggleParentRowLock(item, parentColPath, lock);
        this.toggleChildRowLock(item, parentColPath, lock);

        // Recursively check for grandparents
        this.toggleParentColLock(item, parentColPath, lock);
      }
    },

    toggleChildColLock(item, colPath, lock) {
      const lockedChildColPaths = this.getChildColumnPaths(item, colPath, true);
      const unlockedChildColPaths = this.getChildColumnPaths(item, colPath, false);

      const childColPathsToUpdate = [];

      // If cell is getting unlocked, and all its children are locked, then all children should be unlocked
      if (!lock && isEmpty(unlockedChildColPaths)) {
        childColPathsToUpdate.push(...lockedChildColPaths);
      }

      // If cell is getting locked, and it has a single unlocked child column, then child should also be locked
      if (lock && unlockedChildColPaths.length === 1) {
        childColPathsToUpdate.push(unlockedChildColPaths[0]);
      }

      // If cell has both parent col and parent row locked, then child should also be locked
      const parentRowNodeKey = this.getParentRowKey(this.getKey(item));
      const parentItem = this.anchorMargins.find(row => this.getKey(row) === parentRowNodeKey);
      unlockedChildColPaths.forEach(childColPath => {
        if (this.isCellLocked(parentItem, childColPath)) {
          childColPathsToUpdate.push(childColPath);
        }
      });

      childColPathsToUpdate.forEach(childColPath => {
        this.toggleCellLock(item, childColPath, lock);

        // Toggle sibling, parent and child rows from child column
        this.toggleSiblingRowLock(item, childColPath, lock);
        this.toggleParentRowLock(item, childColPath, lock);
        this.toggleChildRowLock(item, childColPath, lock);

        // Recursively check for grandchildren
        this.toggleChildColLock(item, childColPath, lock);
      });
    },

    toggleSiblingRowLock(item, colPath, lock) {
      const rowNode = this.getRowNode(this.getKey(item));
      if (!rowNode) return;

      const parentRowNode = rowNode.parent;
      const isParentLocked = get(parentRowNode, `data.${colPath}.locked`);

      const unlockedSiblingRowNodes = parentRowNode.childrenAfterFilter.filter(siblingRowNode => {
        return (
          this.getKey(siblingRowNode.data) !== this.getKey(rowNode.data) &&
          this.isCellAvailable(siblingRowNode.data, colPath) &&
          !this.isCellLocked(siblingRowNode.data, colPath)
        );
      });

      /* If cell is getting locked, parent row is locked, and there's only one unlocked sibling row,
       * then that sibling row should be locked.
       */
      if (lock && isParentLocked && unlockedSiblingRowNodes.length === 1) {
        const siblingRowItemToUpdate = this.anchorMargins.find(
          row => this.getKey(row) === this.getKey(unlockedSiblingRowNodes[0].data)
        );
        this.toggleCellLock(siblingRowItemToUpdate, colPath, lock);

        // Toggle sibling, parent and child cols from sibling row
        this.toggleSiblingColLock(siblingRowItemToUpdate, colPath, lock);
        this.toggleParentColLock(siblingRowItemToUpdate, colPath, lock);
        this.toggleChildColLock(siblingRowItemToUpdate, colPath, lock);

        // Toggle parent and child rows from sibling col
        this.toggleParentRowLock(siblingRowItemToUpdate, colPath, lock);
        this.toggleChildRowLock(siblingRowItemToUpdate, colPath, lock);
      }
    },

    toggleParentRowLock(item, colPath, lock) {
      const rowNode = this.getRowNode(this.getKey(item));
      if (!rowNode) return;

      const parentRowNode = rowNode.parent;
      const parentRowNodeKey = this.getKey(parentRowNode.data);
      if (!parentRowNodeKey) return;

      const unlockedSiblingRowNodes = parentRowNode.childrenAfterFilter.filter(siblingRowNode => {
        return (
          this.getKey(siblingRowNode.data) !== this.getKey(rowNode.data) &&
          this.isCellAvailable(siblingRowNode.data, colPath) &&
          !this.isCellLocked(siblingRowNode.data, colPath)
        );
      });

      /* If cell is getting locked, and all sibling rows are also locked, i.e. unlockedSiblingRowNodes is empty,
       * then parent should be locked.
       * If cell is getting unlocked, and all sibling rows are locked, i.e. unlockedSiblingRowNodes is empty,
       * then parent should be unlocked.
       */
      if (isEmpty(unlockedSiblingRowNodes)) {
        const parentRowItemToUpdate = this.anchorMargins.find(
          row => this.getKey(row) === parentRowNodeKey
        );
        this.toggleCellLock(parentRowItemToUpdate, colPath, lock);

        // Toggle sibling, parent and child cols from parent row
        this.toggleSiblingColLock(parentRowItemToUpdate, colPath, lock);
        this.toggleParentColLock(parentRowItemToUpdate, colPath, lock);
        this.toggleChildColLock(parentRowItemToUpdate, colPath, lock);

        // Recursively check for grandparents
        this.toggleParentRowLock(parentRowItemToUpdate, colPath, lock);
      }
    },

    toggleChildRowLock(item, colPath, lock) {
      const rowNode = this.getRowNode(this.getKey(item));
      if (!rowNode) return;

      const lockedChildRowNodes = rowNode.childrenAfterFilter.filter(childRowNode => {
        return (
          this.isCellAvailable(childRowNode.data, colPath) &&
          this.isCellLocked(childRowNode.data, colPath)
        );
      });
      const unlockedChildRowNodes = rowNode.childrenAfterFilter.filter(childRowNode => {
        return (
          this.isCellAvailable(childRowNode.data, colPath) &&
          !this.isCellLocked(childRowNode.data, colPath)
        );
      });

      const childRowNodesToUpdate = [];

      // If cell is getting unlocked, and all its children rows are locked, then all children should be unlocked
      if (!lock && isEmpty(unlockedChildRowNodes)) {
        childRowNodesToUpdate.push(...lockedChildRowNodes);
      }

      // If cell is getting locked, and it has a single unlocked child row, then child should also be locked
      if (lock && unlockedChildRowNodes.length === 1) {
        childRowNodesToUpdate.push(unlockedChildRowNodes[0]);
      }

      // If cell has both parent row and parent col locked, then child should also be locked
      const parentColPath = this.getParentColumnPath(colPath);
      unlockedChildRowNodes.forEach(childRowNode => {
        if (this.isCellLocked(childRowNode.data, parentColPath)) {
          childRowNodesToUpdate.push(childRowNode);
        }
      });

      childRowNodesToUpdate.forEach(childRowNode => {
        const childRowNodeKey = this.getKey(childRowNode.data);
        const childRowItemToUpdate = this.anchorMargins.find(
          row => this.getKey(row) === childRowNodeKey
        );
        this.toggleCellLock(childRowItemToUpdate, colPath, lock);

        // Toggle sibling, parent and child cols from child row
        this.toggleSiblingColLock(childRowItemToUpdate, colPath, lock);
        this.toggleParentColLock(childRowItemToUpdate, colPath, lock);
        this.toggleChildColLock(childRowItemToUpdate, colPath, lock);

        // Recursively check for grandchildren
        this.toggleChildRowLock(childRowItemToUpdate, colPath, lock);
      });
    },

    /* Toggling a cell lock could cause other cells to not be able to cascade updates properly.
     * So we need to identify all cells in the toggled cell tree that also need locking/unlocking so that we
     * enable further updates to work as expected.
     * e.g locking a chain cell that has a single unlocked TSG, that TSG also needs to be locked otherwise the chain cannot be updated.
     * e.g unlocking a chain cell that has a single locked TSG, that TSG also needs to be unlocked otherwise the chain cannot be updated.
     * This function will identify all cell locks that need to be toggled to satisfy the applied lock toggle.
     */
    cascadeLocks({ itemToUpdate, colPath, lock }) {
      this.toggleCellLock(itemToUpdate, colPath, lock);
      this.toggleSiblingColLock(itemToUpdate, colPath, lock);
      this.toggleParentColLock(itemToUpdate, colPath, lock);
      this.toggleChildColLock(itemToUpdate, colPath, lock);
      this.toggleSiblingRowLock(itemToUpdate, colPath, lock);
      this.toggleParentRowLock(itemToUpdate, colPath, lock);
      this.toggleChildRowLock(itemToUpdate, colPath, lock);
    },

    cellLockChanged(params) {
      if (isNil(params.value) || isNil(params.data)) return false;

      const colPath = this.getColPath(params.colDef.colId);
      const itemToUpdate = this.getRowItem(this.anchorMargins, params);

      this.cascadeLocks({
        itemToUpdate,
        rowNode: params.node,
        colPath,
        lock: !this.isLocked(params),
      });

      // Refresh all grid cells
      this.refreshGridCells();
    },

    /* -------------------------------------------------- *
     *           Grid and Cell state functions            *
     * -------------------------------------------------- */

    getDelta(params) {
      const fieldPath = params.colDef.colId;
      const originalValue = get(this.getRowItem(this.originalAnchorMargins, params), fieldPath);
      const newValue = get(params.data, fieldPath);
      if (isNil(originalValue)) return 0;

      return this.format(newValue) - this.format(originalValue);
    },

    hasDiff(params) {
      const originalValue = get(
        this.getRowItem(this.originalAnchorMargins, params),
        params.colDef.colId
      );

      if (isNil(originalValue) || isNil(params.value)) return false;
      if (this.format(originalValue) !== this.format(params.value)) return true;

      return false;
    },

    isLocked(params) {
      if (isNil(params.data)) return false;

      const colPath = params.colDef.colId
        .split('.')
        .slice(0, -1)
        .join('.');

      return get(params.data, `${colPath}.locked`);
    },

    isCellLocked(data, colPath) {
      if (isNil(data)) return false;
      return get(data, `${colPath}.locked`);
    },

    isCellAvailable(data, colPath) {
      if (isNil(data)) return false;
      // Return true if the cell has a valid anchor margin value
      return !isNil(get(data, `${colPath}.anchorMargin`));
    },

    isColLastLevel(colPath) {
      const lastLevel = this.columnTree[this.columnTree.length - 1];
      return colPath.includes(lastLevel);
    },

    /* Checks if a locked cell has had its values re-distributed already. */
    hasLockedCellBeenEvaluated(path, key) {
      /* evaluatedLockedCells contains the colPath of locked columns mapped to a list of evaluated rows,
      /* and the rowNodeKey of locked rows mapped to a list of evaluated cols.
       * If any of the children rows of the current rowNodeKey are in the list,
       * that means we've already redistributed the remainder values of that lock towards its siblings,
       * so no need to do that again when aggregating margins up.
       * If any of the children cols of the current colPath are in the list,
       * that means we've already redistributed the remainder values of that lock towards its siblings,
       * so no need to do that again when aggregating margins up.
       */
      return (this.evaluatedLockedCells[path] || []).some(evaluatedLockedCell =>
        evaluatedLockedCell.includes(key)
      );
    },

    setEvaluatedLockedCell(path, key) {
      if (!this.evaluatedLockedCells[path]) {
        this.evaluatedLockedCells[path] = [];
      }

      // Add the current row or col key to the evaluated locked cell list
      this.evaluatedLockedCells[path].push(key);
    },

    getExpandedState() {
      const expandedNodes = [];
      this.gridApi.forEachNode(node => {
        if (node.expanded) {
          expandedNodes.push(node.id);
        }
      });

      return expandedNodes;
    },

    setExpandedState(expandedNodes) {
      this.gridApi.forEachNode(node => {
        if (expandedNodes.includes(node.id)) {
          node.setExpanded(true);
        }
      });
    },

    setIntermediaryState(params) {
      const colPath = this.getColPath(params.colDef.colId);
      this.intermediaryAnchorMargins = cloneDeep(this.anchorMargins);
      const intermediaryItem = this.getRowItem(this.intermediaryAnchorMargins, params);
      const previousMarginTotal = getMarginTotal({
        marginPercentage: params.oldValue,
        sales: get(intermediaryItem, colPath).sales,
      });
      this.updateCell({
        item: intermediaryItem,
        colPath,
        margin: params.oldValue,
        marginTotal: previousMarginTotal,
      });
    },

    /* -------------------------------------------------- *
     *      Calculation and formatting helper functions   *
     * -------------------------------------------------- */

    /* Gets the margin total leftovers originated from a locked column
     * that could not be updated, weighted based on the current absolute sales per column.
     */
    getMarginTotalRemaindersPerColumn(item, colPaths, marginTotalRemainder) {
      const totals = {};
      colPaths.forEach(colPath => {
        const { sales } = get(item, colPath);
        totals[colPath] = sales || 0;
      });

      return getMarginTotalRemainderPerItem({ salesPerItem: totals, marginTotalRemainder });
    },

    /* Gets the margin total leftovers originated from a locked column
     * that could not be updated, weighted based on the current absolute sales per row column.
     */
    getMarginTotalRemaindersPerRow(rowNodes, colPath, marginTotalRemainder) {
      const totals = {};
      rowNodes.forEach(rowNode => {
        const { sales } = get(rowNode.data, colPath);
        totals[this.getKey(rowNode.data)] = sales || 0;
      });

      return getMarginTotalRemainderPerItem({ salesPerItem: totals, marginTotalRemainder });
    },

    /* Round numbers to 1 decimal place */
    format(value) {
      if (isNaN(value)) {
        return value;
      }

      if (isNil(value)) {
        return;
      }

      return round(value, 1);
    },

    /* -------------------------------------------------- *
     *       Action functions (Save, Reset, Toggle)       *
     * -------------------------------------------------- */

    async saveChanges() {
      // Set saving state to disable UI and show spinner
      this.isSaving = true;

      if (this.gridApi) {
        this.gridApi.showLoadingOverlay();
      }

      try {
        await this.updateAnchorMargins({
          updates: this.getDeepestAggregationLevelRows(),
          cascadeProportionately: this.cascadeProportionately,
        });
        await this.resetGrid({ forceDataRefresh: true });
      } catch (error) {
        console.error('Error saving changes:', error);
        if (this.gridApi) {
          this.gridApi.hideOverlay();
        }
      } finally {
        this.isSaving = false;
      }
    },

    async resetGrid({ forceDataRefresh = false }) {
      // Add loading overlay for data refresh
      if (forceDataRefresh && this.gridApi) {
        this.gridApi.showLoadingOverlay();
      }

      // Get current expanded state
      const expandedState = this.getExpandedState();

      try {
        if (forceDataRefresh) {
          // Fetch latest values
          await this.fetchGridData();
        } else {
          // Re-apply original values
          this.anchorMargins = cloneDeep(this.originalAnchorMargins);
          this.setGridData(this.anchorMargins);
        }

        // Reset grid rows
        this.refreshGridCells();
        // Re-apply expanded state
        this.setExpandedState(expandedState);
        // Clear edits
        this.editsExist = false;
        // Edit mode is enabled by default
        this.editMode = true;

        // Hide loading overlay if not handled by fetchGridData
        if (!forceDataRefresh && this.gridApi) {
          this.gridApi.hideOverlay();
        }
      } catch (error) {
        console.error('Error resetting grid:', error);
        if (this.gridApi) {
          this.gridApi.hideOverlay();
        }
      }
    },

    toggleEditMode() {
      // Required for CellEditor cellRendererParams to be re-evaluated with new editMode value
      this.refreshGridCells();
    },

    /* -------------------------------------------------- *
     *               Styling functions                    *
     * -------------------------------------------------- */

    tooltipValueGetter(params) {
      const fieldPath = params.colDef.colId;
      const colPath = this.getColPath(fieldPath);
      const originalItem = this.getRowItem(this.originalAnchorMargins, params);
      if (isNil(originalItem)) return null;

      const sales = get(params.data, `${colPath}.sales`) || 0;
      const anchorMarginTotal = get(params.data, `${colPath}.anchorMarginTotal`) || 0;
      const originalAnchorMargin = get(originalItem, `${colPath}.anchorMargin`) || 0;
      const originalMarginTotal = get(originalItem, `${colPath}.anchorMarginTotal`) || 0;
      const productCount = get(originalItem, `${colPath}.productCount`) || 0;

      const additionalText = [
        `Previous Anchor Margin: ${this.format(originalAnchorMargin)}`,
        `Current Margin total: ${this.format(anchorMarginTotal)}`,
        `Previous Margin total: ${this.format(originalMarginTotal)}`,
        `Current sales: ${this.format(sales)}`,
        `Products: ${productCount}`,
      ];
      return {
        additionalText,
      };
    },

    getCellStyle(params) {
      const originalValue = get(
        this.getRowItem(this.originalAnchorMargins, params),
        params.colDef.colId
      );

      if (isNil(originalValue)) {
        // Prevent updates on cells that do not contain data
        return { pointerEvents: 'none' };
      }

      return null;
    },

    configureGridExports() {
      const overrideExportMethod = (originalMethod, exportOptions) => {
        const columnsToExport = this.gridColumnApi
          .getAllGridColumns()
          .filter(column => column.getColId() !== 'ag-Grid-AutoColumn')
          .map(column => column.getColId());

        const exportParams = {
          ...exportOptions,
          columnKeys: columnsToExport,
          processCellCallback: params => {
            if (params.column.colId.includes('anchorMargin')) {
              return this.format(params.value);
            }
            return params.value;
          },
        };

        originalMethod.call(this.gridApi, exportParams);
      };

      const originalExportDataAsCsv = this.gridApi.exportDataAsCsv;
      const originalExportDataAsExcel = this.gridApi.exportDataAsExcel;

      // Override export methods with our custom implementation
      this.gridApi.exportDataAsCsv = exportOptions => {
        overrideExportMethod(originalExportDataAsCsv, exportOptions);
      };

      this.gridApi.exportDataAsExcel = exportOptions => {
        overrideExportMethod(originalExportDataAsExcel, exportOptions);
      };
    },
  },
};
</script>

<style lang="scss">
@import '@style/base/_variables.scss';
::v-deep {
  .ag-anchor-margins-grid {
    /* ==========================================
       Header Styles
       Controls the appearance of column headers
       ========================================== */
    &__header,
    .ag-header-cell {
      .ag-cell-label-container {
        flex-direction: column-reverse;
        align-items: start;

        .ag-header-cell-menu-button {
          margin-left: -2px;
        }

        .ag-header-cell-text {
          font-weight: bold;
        }
      }
    }

    /* ==========================================
       Header Group Styles
       Styles for grouped headers in the grid
       ========================================== */
    .ag-header-group-cell {
      border-left: 1px solid $custom-ag-grid-header-separator-color;

      .ag-header-cell-resize::after {
        visibility: hidden;
      }

      &:last-child {
        border-right: 1px solid $custom-ag-grid-header-separator-color;
      }

      &-first {
        border-left: 1px solid $custom-ag-grid-header-separator-color;
      }

      &-last {
        &:last-child {
          border-right: 1px solid $custom-ag-grid-header-separator-color;
        }

        .ag-header-cell-resize::after {
          visibility: hidden;
        }
      }
    }

    /* ==========================================
       Cell Styles
       Base styles for grid cells
       ========================================== */
    .ag-cell {
      display: inline-block;
      cursor: pointer;
      color: rgba(0, 0, 0, 0.87);
      border-bottom: none;
      border-top: none;

      &.ag-group-cell {
        &-first {
          border-left: 1px solid $custom-ag-grid-header-separator-color;
        }
      }

      &.ag-column-last {
        border-right: 1px solid $custom-ag-grid-header-separator-color;
      }

      &.font-weight-bold {
        font-weight: bold;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
    }

    /* ==========================================
       Row Styles
       Controls the appearance of grid rows
       ========================================== */
    .ag-row {
      border-bottom: none;
      border-top: none;

      &.ag-row-even {
        background: $custom-ag-grid-even-rows-background-color;
      }

      &.ag-row-odd {
        background: $custom-ag-grid-odd-rows-background-color;
      }

      &:hover {
        background-color: $grid-view-row-promo-dense-hover-color !important;

        .ag-cell.proposed-pricing-cell {
          background: $grid-view-row-promo-dense-hover-color !important;
        }
      }
    }

    /* ==========================================
       Utility Classes
       Reusable utility classes
       ========================================== */
    .alerts-cell {
      display: flex;
      overflow: visible;
      padding: 0 8px 0 4px;
    }

    .cell-resize-invisible {
      .ag-header-cell-resize::after {
        visibility: hidden;
      }
    }
  }
}

.diff-background {
  background-color: $ag-grid-edit-background-color;

  &.ag-cell-focus.ag-cell-not-inline-editing {
    background-color: $ag-grid-edit-background-color !important;
  }
}

.locked-background {
  background-color: $ag-grid-lock-background-color;

  &.ag-cell-focus.ag-cell-not-inline-editing {
    background-color: $ag-grid-lock-background-color !important;
  }
}

.toggles {
  display: flex;

  .switch-prefix {
    display: inline-flex;
    height: 2rem;
    align-items: self-end;
  }
}

.anchor-margins-footer {
  height: 50px;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  gap: 16px;
  border-bottom: 1px solid $grid-header-footer-border-color;

  &__btn-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .v-text-field__details {
    display: none;
  }
}

.custom-tooltip {
  min-width: 50px;
  padding: 5px;
  display: flex;
  background-color: white;
  border: 2px solid #ccc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  .entry {
    min-width: 50px;
    display: flex;
    flex-direction: column;
  }
}
</style>
